import { FB_DELETE_CONTENT_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { StageType, FBRecommendationKey } from '$extensionSrc/utils/enums'

import { StageDef } from '../automationMachineInterfaces'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  getFacebookDeleteBeginUpdateStageAction,
  getFacebookDeleteCleanupAction,
  getFacebookDeleteContentCategoryForMonthStageAction,
  getFacebookDeleteContentFinishStageAction,
  getFacebookLoadDeleteContentCategoryStageAction,
  getFacebookLoadOldestPostStageAction,
  getFacebookSaveOldestPostStageAction,
} from './fbContentUtils'
import { FacebookContentCategoryKey } from './fbUrls'

const getCurrentValueFn = () => () => ''

const getIsFulfilledFn = () => () => false
const settingsToContentType = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]:
    FacebookContentCategoryKey.COMMENTS_ONLY,
  [FacebookSettingKey.DELETE_LIKES]: FacebookContentCategoryKey.REACTIONS_ONLY,
}

const deletePostLabels = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]: ['remove'],
  [FacebookSettingKey.DELETE_LIKES]: ['remove'],
}

const deletePostModalTitles = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]: 'remove interaction?',
  [FacebookSettingKey.DELETE_LIKES]: 'remove interaction?',
}

const confirmDeleteLabels = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]: 'remove',
  [FacebookSettingKey.DELETE_LIKES]: 'remove',
}

const deletePostPageTitles = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]: 'comments',
  [FacebookSettingKey.DELETE_LIKES]: 'likes and reactions',
}

const postTypes = {
  [FacebookSettingKey.DELETE_COMMENTS_FB]: 'comments',
  [FacebookSettingKey.DELETE_LIKES]: 'likes and reactions',
}

// Tasks require initial values in a single setting key
const initialValuesSettingKey = FacebookSettingKey.DELETE_COMMENTS_FB
const contentType = 'interactions'
const mainPageCategory = FacebookContentCategoryKey.COMMENTS_REACTIONS
const mainPageTitle = 'comments and reactions'

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_INTERACTIONS_LOAD_ACTIVITY_LOG`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookLoadOldestPostStageAction(),
  },
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_INTERACTIONS_GET_OLDEST_ACTIVITY`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookSaveOldestPostStageAction(
      initialValuesSettingKey,
      FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES,
    ),
  },
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_INTERACTIONS_CLEANUP`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_END,
    action: getFacebookDeleteCleanupAction(
      FacebookContentCategoryKey.COMMENTS_REACTIONS,
      'comments and reactions',
    ),
  },
]

const getCantUndoFn = () => () => true

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_INTERACTIONS_LOAD_ACTIVITY_LOG`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookDeleteBeginUpdateStageAction(
      FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES,
    ),
  },
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_INTERACTIONS_GET_OLDEST_ACTIVITY`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookSaveOldestPostStageAction(
      initialValuesSettingKey,
      FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES,
    ),
  },
  // Create flat array by adding each stage definition directly
  ...Object.keys(settingsToContentType).flatMap((s: FacebookSettingKey) => [
    {
      type: StageType.ACTION,
      key: `UPDATE_LOAD_${s}`,
      timeout: FB_DELETE_CONTENT_TIMEOUT,
      action: getFacebookLoadDeleteContentCategoryStageAction(
        initialValuesSettingKey,
        settingsToContentType[s],
      ),
    },
    {
      type: StageType.ACTION,
      key: `UPDATE_MONTH_${s}`,
      timeout: FB_DELETE_CONTENT_TIMEOUT,
      action: getFacebookDeleteContentCategoryForMonthStageAction(
        settingsToContentType[s],
        s,
        FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES,
        postTypes[s],
        deletePostPageTitles[s],
        deletePostLabels[s],
        deletePostModalTitles[s],
        confirmDeleteLabels[s],
        contentType,
        initialValuesSettingKey,
      ),
    },
  ]),
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_INTERACTIONS_FINISH`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_END,
    action: getFacebookDeleteContentFinishStageAction(
      FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES,
      initialValuesSettingKey,
      contentType,
      mainPageCategory,
      mainPageTitle,
    ),
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
